<div class="map-page">
  @if (dropPinMode()) {
    <div class="drop-pin-hint" data-testid="drop-pin-hint">
      {{ 'map.dropPin.hint' | translate }}
    </div>
  }

  @if (loadingTap()) {
    <div class="map-loading-overlay" data-testid="map-loading">
      <app-icon name="loader" [size]="28" />
    </div>
  }

  <div class="map-search-container" data-testid="map-search-container">
    <div class="active-range-label" data-testid="active-range-label">{{ activeRangeLabel() }}</div>
    <div
      class="search-input-wrapper"
      [clickOutsideEnabled]="showSearchResults() || !!searchQuery()"
      (clickOutside)="clearSearch()"
    >
      <app-icon name="search" [size]="18" class="search-icon" />
      <input
        type="text"
        class="map-search-input"
        [value]="searchQuery()"
        (input)="onSearchInput($event.target.value)"
        [placeholder]="'map.search.placeholder' | translate"
        data-testid="map-search-input"
      />
      @if (searchQuery()) {
        <button
          type="button"
          class="search-clear-btn"
          (click)="clearSearch()"
          [attr.aria-label]="'button.close' | translate"
          data-testid="map-search-clear"
        >
          <app-icon name="x" [size]="18" />
        </button>
      }
      @if (showSearchResults()) {
        <div class="search-results-dropdown" data-testid="search-results-dropdown">
          @if (searchLoading()) {
            <div class="search-results-loading">
              <app-icon name="loader" [size]="16" />
            </div>
          } @else if (searchResults().length > 0) {
            <ul class="search-results-list">
              @for (result of searchResults(); track result.address) {
                <li>
                  <button
                    type="button"
                    class="search-result-item"
                    (click)="selectSearchResult(result)"
                    [attr.data-testid]="'search-result-' + $index"
                  >
                    <app-icon name="map-pin" [size]="14" class="result-icon" />
                    <span class="result-text">{{ result.address }}</span>
                  </button>
                </li>
              }
            </ul>
          } @else {
            <div class="search-results-empty">{{ 'map.search.noResults' | translate }}</div>
          }
        </div>
      }
    </div>
  </div>

  <div class="map-canvas-wrap">
    <div #mapEl class="map-canvas" data-testid="map-canvas"></div>

    @if (selectedPoi(); as poi) {
      <div
        class="poi-popup map-flyout-panel"
        [style.left.px]="poi.point.x"
        [style.top.px]="poi.point.y"
        data-testid="poi-popup"
      >
        <button
          type="button"
          class="poi-popup-close"
          (click)="closePoiPopup()"
          [attr.aria-label]="'map.poi.close' | translate"
          data-testid="poi-popup-close"
        >
          <app-icon name="x" [size]="14" />
        </button>
        <div class="poi-popup-header">
          <app-icon [name]="poiIcon(poi.poiClass, poi.subclass)" [size]="16" />
          <span class="poi-popup-name">{{ poi.name || ('map.poi.unnamed' | translate) }}</span>
        </div>
        @if (poi.subclass) {
          <div class="poi-popup-category">{{ poi.subclass.replaceAll('_', ' ') }}</div>
        }
      </div>
    }
  </div>

  <div #actionsCtrl class="maplibregl-ctrl maplibregl-ctrl-group actions-ctrl">
    <button
      type="button"
      class="action-toggle"
      [class.active]="dropPinMode()"
      [tippy]="(dropPinMode() ? 'map.dropPin.active' : 'map.dropPin.button') | translate"
      [attr.aria-label]="(dropPinMode() ? 'map.dropPin.active' : 'map.dropPin.button') | translate"
      (click)="toggleDropPin()"
      data-testid="drop-pin-toggle"
    >
      <app-icon name="map-pin" [size]="18" />
    </button>
    <button
      type="button"
      class="action-toggle"
      [class.active]="activityFilterOpen()"
      [tippy]="'map.activityFilter.button' | translate"
      [attr.aria-label]="'map.activityFilter.button' | translate"
      [attr.aria-expanded]="activityFilterOpen()"
      (click)="toggleActivityFilter()"
      data-testid="activity-filter-toggle"
    >
      <app-icon name="funnel" [size]="18" />
    </button>
    @if (activityFilterOpen()) {
      <div
        class="map-flyout-panel activity-filter-panel"
        (clickOutside)="closeActivityFilter()"
        data-testid="activity-filter-panel"
      >
        <div class="map-flyout-title">{{ 'map.activityFilter.title' | translate }}</div>
        <form [formGroup]="dateForm" class="date-form">
          <formly-form [form]="dateForm" [fields]="dateFields" [model]="dateModel" />
        </form>
      </div>
    }
  </div>

  <div
    #styleCtrl
    class="maplibregl-ctrl maplibregl-ctrl-group style-ctrl"
    (mouseenter)="onStyleCtrlMouseEnter()"
    (mouseleave)="onStyleCtrlMouseLeave()"
  >
    <button
      type="button"
      class="style-toggle"
      [attr.aria-label]="'map.style.button' | translate"
      [attr.aria-expanded]="showStylePicker()"
      (click)="onStyleToggleClick()"
      (pointerdown)="onStylePointerDown($event)"
      (pointerup)="onStylePointerUp()"
      (pointercancel)="onStylePointerUp()"
      (pointerleave)="onStylePointerUp()"
      data-testid="style-toggle"
    >
      <span class="style-swatch" [class]="nextStyleId()"></span>
    </button>
    @if (showStylePicker()) {
      <div class="map-flyout-panel" data-testid="style-panel">
        <div class="map-flyout-title">{{ 'map.style.title' | translate }}</div>
        @for (id of mapStyles; track id) {
          <button
            type="button"
            class="map-flyout-option"
            [class.selected]="id === mapStyleId()"
            (click)="selectMapStyle(id)"
            [attr.data-testid]="'style-option-' + id"
          >
            <span class="style-swatch" [class]="id"></span>
            {{
              (id === 'streets' ? 'map.style.streets' : id === 'satellite' ? 'map.style.satellite' : 'map.style.hybrid')
                | translate
            }}
          </button>
        }
      </div>
    }
  </div>

  <div #layersCtrl class="maplibregl-ctrl maplibregl-ctrl-group layers-ctrl">
    <button
      type="button"
      class="layers-toggle"
      [class.active]="layersOpen()"
      [attr.aria-label]="'map.layers.button' | translate"
      [attr.aria-expanded]="layersOpen()"
      (click)="toggleLayersPanel()"
      data-testid="layers-toggle"
    >
      <app-icon name="layers" [size]="18" />
    </button>
    @if (layersOpen()) {
      <div class="map-flyout-panel" data-testid="layers-panel">
        <div class="map-flyout-title">{{ 'map.layers.title' | translate }}</div>
        <button
          type="button"
          class="map-flyout-option"
          [class.selected]="activityLayerVisible()"
          (click)="toggleActivityLayer()"
          data-testid="layers-activity-toggle"
        >
          <app-icon [name]="activityLayerVisible() ? 'square-check' : 'square'" [size]="14" />
          {{ 'map.layers.activity' | translate }}
        </button>
      </div>
    }
  </div>
</div>

@if (showUnitPicker()) {
  <app-modal-dialog (backdropClick)="cancelUnitPicker()">
    <div header>
      <div class="heading-md">{{ 'map.unitPicker.title' | translate }}</div>
    </div>
    <div content>
      <ul class="unit-picker-list" data-testid="unit-picker-list">
        @for (row of pickerRows(); track row.registryUnit.id) {
          <li>
            <button
              type="button"
              class="unit-picker-row"
              (click)="pickUnit(row)"
              [attr.data-testid]="'unit-picker-row-' + row.registryUnit.id"
            >
              <span class="unit-label">
                {{ row.registryUnit.unit ? ('map.unitPicker.unitLabel' | translate: { unit: row.registryUnit.unit }) : row.registryUnit.formatted_address }}
              </span>
              @if (row.localAddress?.last_outcome_kind) {
                <app-outcome-chip [outcome]="row.localAddress!.last_outcome_kind" [objectionId]="row.localAddress!.last_objection_id" />
              } @else {
                <span class="unit-new-badge">{{ 'map.unitPicker.new' | translate }}</span>
              }
            </button>
          </li>
        }
      </ul>
    </div>
    <div footer>
      <app-button variant="text" (click)="cancelUnitPicker()" data-testid="cancel-unit-picker-button">
        {{ 'map.unitPicker.cancel' | translate }}
      </app-button>
    </div>
  </app-modal-dialog>
}

<router-outlet />
